import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Save, Send, Loader2, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const AdminSmtp = () => {
  const [data, setData] = useState({
    host: 'smtp.ionos.de',
    port: 587,
    username: 'info@dalaa-beauty.de',
    password: '',
    from_email: 'info@dalaa-beauty.de',
    to_email: 'info@dalaa-beauty.de',
    use_tls: true,
  });
  const [originalPwd, setOriginalPwd] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    api.get('/admin/smtp')
      .then((r) => {
        setData((d) => ({ ...d, ...r.data }));
        setOriginalPwd(r.data?.password || '');
      })
      .finally(() => setLoading(false));
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/smtp', data);
      toast.success('SMTP settings saved');
      // refresh to show masked password again
      const r = await api.get('/admin/smtp');
      setData((d) => ({ ...d, ...r.data }));
      setOriginalPwd(r.data?.password || '');
      setShowPwd(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    // Save first if user has unsaved changes
    if (data.password !== originalPwd || data.host || data.port) {
      // best to save first
    }
    setTesting(true);
    try {
      const r = await api.post('/admin/smtp/test');
      toast.success(r.data?.message || 'Test email sent successfully');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-smtp-page">
      <div>
        <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Email / SMTP Settings</h1>
        <p className="text-gray-500 text-sm">Configure outgoing mail. The contact form and bookings will use these credentials to notify you.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-900">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <strong>Security note:</strong> Your SMTP password is stored encrypted at rest in MongoDB. After saving, it is masked as <code className="bg-amber-100 px-1 rounded">********</code> and never returned to the browser again. To replace it, just type a new password and click Save.
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <h2 className="font-display text-lg text-[#5a1d3b]" style={{ fontWeight: 500 }}>Server</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-2 block text-sm">SMTP Host</Label>
            <Input value={data.host || ''} onChange={(e) => update('host', e.target.value)} className="h-11" placeholder="smtp.ionos.de" data-testid="smtp-host-input" />
          </div>
          <div>
            <Label className="mb-2 block text-sm">SMTP Port</Label>
            <Input
              type="number"
              value={data.port ?? 587}
              onChange={(e) => update('port', parseInt(e.target.value || '0', 10))}
              className="h-11"
              placeholder="587"
              data-testid="smtp-port-input"
            />
          </div>
        </div>

        <div>
          <Label className="mb-2 block text-sm">SMTP Username</Label>
          <Input value={data.username || ''} onChange={(e) => update('username', e.target.value)} className="h-11" data-testid="smtp-username-input" />
        </div>

        <div>
          <Label className="mb-2 block text-sm">SMTP Password</Label>
          <div className="relative">
            <Input
              type={showPwd ? 'text' : 'password'}
              value={data.password || ''}
              onChange={(e) => update('password', e.target.value)}
              className="h-11 pr-10"
              placeholder={originalPwd === '********' ? 'Saved (hidden) — type to replace' : 'Enter password'}
              data-testid="smtp-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Leave as &quot;********&quot; to keep the existing password.</p>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Switch
            checked={!!data.use_tls}
            onCheckedChange={(v) => update('use_tls', v)}
            id="use-tls"
          />
          <Label htmlFor="use-tls" className="text-sm">Use TLS / STARTTLS</Label>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
        <h2 className="font-display text-lg text-[#5a1d3b]" style={{ fontWeight: 500 }}>Addresses</h2>
        <div>
          <Label className="mb-2 block text-sm">From Email (sender shown to recipients)</Label>
          <Input type="email" value={data.from_email || ''} onChange={(e) => update('from_email', e.target.value)} className="h-11" data-testid="smtp-from-email-input" />
        </div>
        <div>
          <Label className="mb-2 block text-sm">To Email (where notifications are delivered)</Label>
          <Input type="email" value={data.to_email || ''} onChange={(e) => update('to_email', e.target.value)} className="h-11" data-testid="smtp-to-email-input" />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-between items-center">
        <Button
          type="button"
          variant="outline"
          onClick={sendTest}
          disabled={testing || saving}
          className="rounded-full h-12 px-6 gap-2 border-[#c4477a] text-[#c4477a] hover:bg-[#fdf2f5]"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send Test Email
        </Button>

        <Button
          onClick={save}
          disabled={saving || testing}
          data-testid="smtp-save-btn"
          className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-12 px-8 gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </Button>
      </div>

      <p className="text-xs text-gray-500 flex items-center gap-1.5 pt-2">
        <Mail className="w-3.5 h-3.5" />
        Once saved, your contact form and booking submissions will be emailed to <strong className="text-gray-700">{data.to_email}</strong>.
      </p>
    </div>
  );
};

export default AdminSmtp;
